// Helpers for text templates (pasted text with merge-field chips).
//
// A field gets its value from exactly one place:
//   bullhorn - a placement field in Bullhorn, filled when the document is sent
//   auto     - an automatic date (sent today, or the day the candidate signs)
//   manual   - typed in by the sender when sending (required before it can go)

const DATE_TYPES = new Set(['timestamp', 'datetime', 'date']);

export const AUTOMATIC_FIELDS = [
  { source: 'sent_date', label: 'Date sent', hint: 'Today’s date, when you send it' },
  { source: 'completion_date', label: 'Date signed', hint: 'The date the candidate signs' },
];

export const MANUAL_FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
];

// The template field type a Bullhorn field should carry. Dates are formatted as
// dates when the document is filled in; everything else is plain text.
export function fieldTypeForBullhorn(dataType) {
  return DATE_TYPES.has(String(dataType || '').toLowerCase()) ? 'date' : 'text';
}

// Which of the three kinds a saved or in-progress field is.
export function fieldKind(f) {
  if (f.kind) return f.kind;
  if (f.mapped_bullhorn_field || f.bullhorn_source_field) return 'bullhorn';
  if (f.date_source === 'sent_date' || f.date_source === 'completion_date') return 'auto';
  return 'manual';
}

// Merge-field ids a template's HTML refers to, in document order, de-duplicated.
export function mergeFieldIds(html) {
  const seen = new Set();
  const out = [];
  for (const m of String(html || '').matchAll(/data-merge-field="([^"]+)"/g)) {
    if (!seen.has(m[1])) {
      seen.add(m[1]);
      out.push(m[1]);
    }
  }
  return out;
}

// The fields to save: exactly the ones the text still uses, in order of first
// appearance. Fields whose chips were deleted drop out, so the template never
// carries a required field nothing refers to. Every field is required.
export function usedContractFields(html, fieldsById) {
  return mergeFieldIds(html)
    .map((id) => fieldsById[id])
    .filter(Boolean)
    .map((f, position) => {
      const kind = fieldKind(f);
      return {
        id: f.id,
        label: f.label,
        field_type: kind === 'auto' ? 'date' : f.field_type || 'text',
        required: true,
        mapped_bullhorn_field: kind === 'bullhorn' ? f.mapped_bullhorn_field : '',
        date_source: kind === 'auto' ? f.date_source : null,
        sender_fillable: kind === 'manual',
        write_back_to_bullhorn: false,
        candidate_editable: false,
        position,
      };
    });
}

// A stable client-side id for a new field.
export function newFieldId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `f_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

// ---------------------------------------------------------------------------
// The colour cue for a field, so its type can be told at a glance: a letter in a
// coloured box in the field list, and the same colour on its chip in the text.
//   Text = blue, Date = green, Number = orange, Automatic = purple, Bullhorn = beige.
// A field that comes from Bullhorn is beige and marked B whatever its type, and an
// automatic field (the sent/signed dates) is purple whatever its type.
// ---------------------------------------------------------------------------
export const FIELD_CUES = {
  text: { letter: 'T', label: 'Text', badge: 'bg-sky-600 text-white' },
  date: { letter: 'D', label: 'Date', badge: 'bg-green-600 text-white' },
  number: { letter: 'N', label: 'Number', badge: 'bg-orange-600 text-white' },
  auto: { letter: 'A', label: 'Automatic', badge: 'bg-purple-600 text-white' },
  bullhorn: { letter: 'B', label: 'Bullhorn', badge: 'bg-[#d9c8a0] text-[#4a3b1c]' },
};

export function fieldCue(f) {
  if (!f) return 'text';
  if (fieldKind(f) === 'bullhorn') return 'bullhorn';
  if (fieldKind(f) === 'auto') return 'auto';
  if (f.field_type === 'date') return 'date';
  if (f.field_type === 'number') return 'number';
  return 'text';
}

// How the sender's fill-in preview draws the fields (the server wraps each one in
// <span data-filled="true"> with its value, or <span data-filled="empty"> with the field's
// name when it has no value yet): a light blue box round the value, and the name in pale blue
// text while it is still empty.
export const FILLED_FIELD_CLASSES =
  '[&_[data-filled]]:rounded [&_[data-filled]]:border [&_[data-filled]]:border-sky-200 [&_[data-filled]]:bg-sky-100 ' +
  '[&_[data-filled]]:px-1 [&_[data-filled]]:[box-decoration-break:clone] ' +
  '[&_[data-filled=empty]]:bg-sky-50 [&_[data-filled=empty]]:text-sky-300';
